import { ArrowDown, ArrowUp, ChevronRight } from 'lucide-react';

import { formatCurrency } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';

import { useGroupSpendingTrendsQuery } from '@/hooks/queries/useReports';

const TREND_COLORS = [
  'bg-linear-to-r from-blue-500 to-blue-600',
  'bg-linear-to-r from-green-500 to-green-600',
  'bg-linear-to-r from-purple-500 to-purple-600',
  'bg-linear-to-r from-orange-500 to-orange-600',
  'bg-linear-to-r from-pink-500 to-pink-600',
  'bg-linear-to-r from-indigo-500 to-indigo-600',
];

interface MonthlyTrendsProps {
  groupId: string;
  startMonth: string;
  endMonth: string;
  currency: string;
  onSelectMonth?: (month: string) => void;
}

export default function MonthlyTrends({
  groupId,
  startMonth,
  endMonth,
  currency,
  onSelectMonth,
}: MonthlyTrendsProps) {
  const { t, i18n } = useTranslation('reports');
  const { data: trends, isLoading } = useGroupSpendingTrendsQuery(
    groupId,
    startMonth,
    endMonth
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SplitFiestaLoading className="bg-transparent" />
      </div>
    );
  }

  if (!trends || trends.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('noData')}
      </div>
    );
  }

  // Calculate max amount for chart scaling
  const maxAmount = Math.max(...trends.map(t => t.totalAmount));

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
        {t('trends.title')}
      </h2>

      {/* Trend Chart */}
      <div className="space-y-4">
        {trends
          .slice()
          .reverse()
          .map((trend, reversedIndex) => {
            const barWidth =
              maxAmount > 0 ? (trend.totalAmount / maxAmount) * 100 : 0;
            const isIncrease = trend.changeFromPrevious.amountChange > 0;
            const colorClass =
              TREND_COLORS[reversedIndex % TREND_COLORS.length];

            // Show change indicator for all months except the most recent (first in reversed order)
            const showChangeIndicator = reversedIndex < trends.length - 1;

            const label = (() => {
              const [year, month] = trend.month.split('-');
              return new Date(
                parseInt(year),
                parseInt(month) - 1,
                1
              ).toLocaleDateString(i18n.language, {
                month: 'long',
                year: 'numeric',
              });
            })();

            const detailId = `month-detail-${trend.month}`;
            const row = (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-4 h-4 shrink-0 rounded-full ${colorClass}`}
                    />
                    <div className="text-left min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {label}
                      </p>
                      <p
                        id={detailId}
                        className="text-xs sm:text-sm text-gray-500 dark:text-gray-400"
                      >
                        {trend.expenseCount} {t('trends.expenses')}
                        {showChangeIndicator && (
                          <>
                            {' • '}
                            <span
                              className={`inline-flex items-center gap-1 ${
                                isIncrease
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-green-600 dark:text-green-400'
                              }`}
                            >
                              {isIncrease ? (
                                <ArrowUp
                                  className="h-3 w-3"
                                  aria-hidden="true"
                                />
                              ) : (
                                <ArrowDown
                                  className="h-3 w-3"
                                  aria-hidden="true"
                                />
                              )}
                              {trend.changeFromPrevious.amountPercentChange > 0
                                ? '+'
                                : ''}
                              {trend.changeFromPrevious.amountPercentChange.toFixed(
                                1
                              )}
                              %
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm lg:text-base">
                        {formatCurrency(trend.totalAmount, currency)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {barWidth.toFixed(1)}%
                      </p>
                    </div>
                    {onSelectMonth ? (
                      <ChevronRight
                        aria-hidden="true"
                        data-testid={`month-open-hint-${trend.month}`}
                        className="w-4 h-4 text-gray-400"
                      />
                    ) : null}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${colorClass} transition-all duration-500`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </>
            );

            return onSelectMonth ? (
              <button
                key={trend.month}
                type="button"
                onClick={() => onSelectMonth(trend.month)}
                aria-label={t('drilldown.openMonth', { month: label })}
                // The label replaces the row's text, so the counts still need announcing.
                aria-describedby={detailId}
                className="w-full text-left space-y-2 rounded-lg -mx-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {row}
              </button>
            ) : (
              <div key={trend.month} className="space-y-2">
                {row}
              </div>
            );
          })}
      </div>

      {/* Summary */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('trends.summary')}
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {formatCurrency(
              trends.reduce((sum, t) => sum + t.totalAmount, 0),
              currency
            )}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t('trends.average')}
            </p>
            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
              {formatCurrency(
                trends.reduce((sum, t) => sum + t.totalAmount, 0) /
                  trends.length,
                currency
              )}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t('trends.highest')}
            </p>
            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
              {formatCurrency(maxAmount, currency)}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t('trends.lowest')}
            </p>
            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
              {formatCurrency(
                Math.min(...trends.map(t => t.totalAmount)),
                currency
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
